import Utils from "./utils";

export default class Bazaar {
    static customPackageToBazaar(type, custom) {
        return {
            authors: [custom.author],
            description: custom.description,
            installs: 0,
            languages: [],
            latest: null,
            creator: custom.creator,
            media: custom.media || [],
            name: custom.id || custom.name,
            short_description: custom.description,
            systems: null,
            tags: {},
            title: custom.title,
            type: type,
            created: 0,
            updated: 0,
            url: custom.url,
            manifest: Bazaar.getPackageManifestUrl(custom.manifest, false),
            locked: custom.locked,
            versions: [custom.version],
            installed: custom.version,
            secret: custom.secret,
            processing: false,
            entitled: false,
            purchased: false,
            fullyLoaded: true,
            custom: true,
        };
    }
    static hashProps(hash) {
        return hash
            .slice(1)
            .split("&")
            .reduce((props, prop) => {
                const [key, value] = prop.split("=");
                if (key && value) {
                    props[decodeURIComponent(key)] = decodeURIComponent(value);
                }
                return props;
            }, {});
    }
    static buildHash(hashProps, overrides = {}) {
        const properties = Object.assign({}, hashProps, overrides);
        return Object.entries(properties)
            .filter(([k, v]) => k && v)
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
            .join("&");
    }

    static bazaarLink(pkg, showPackage = false, gift = null) {
        let hash = {};
        if (pkg) {
            switch (pkg.type) {
                case "module":
                case "system":
                case "world":
                    hash.foundry = pkg.type;
                    break;
                case "asset":
                case "forge-gift-card":
                    hash.forge = pkg.type;
                    break;
                case "dndbeyond":
                    hash.is = pkg.type;
                    break;
            }
            if (pkg.premium) {
                hash.price = "premium";
            }
            if (showPackage) {
                hash.package = pkg.slug || pkg.name;
                hash.custom = pkg.custom;
            }
            if (gift != null) {
                hash.purchase = gift ? "gift" : "true";
            }
        }

        return `/bazaar#${Utils.buildHash(hash)}`;
    }

    /**
     * Finds a package in the sourcePackages array that matches the targetPackage.
     *
     * @param {Object} targetPackage - The package to find in the sourcePackages array.
     * @param {string} targetPackage.type - The type of the target package.
     * @param {string} targetPackage.name - The name of the target package.
     * @param {boolean} [targetPackage.custom] - Indicates if the target package is custom.
     * @param {Array<Object>} sourcePackages - The array of source packages to search through.
     * @param {string} sourcePackages[].type - The type of a source package.
     * @param {string} sourcePackages[].name - The name of a source package.
     * @param {boolean} [sourcePackages[].custom] - Indicates if a source package is custom.
     * @returns {Object|undefined} The matching package from the sourcePackages array, or undefined if no match is found.
     */
    static findPackage(targetPackage, sourcePackages) {
        return sourcePackages.find(
            (sourcePackage) =>
                sourcePackage.type === targetPackage.type &&
                sourcePackage.name === targetPackage.name &&
                Boolean(sourcePackage.custom) === Boolean(targetPackage.custom)
        );
    }

    /**
     * Generates a unique key for a given package based on its type and name.
     * If the package is custom, appends '-custom' to the key.
     *
     * @param {Object} pkg - The package object.
     * @param {string} pkg.type - The type of the package.
     * @param {string} pkg.name - The name of the package.
     * @param {boolean} [pkg.custom] - Indicates if the package is custom.
     * @returns {string} The generated key for the package.
     */
    static getDisplayedPackageKey(pkg) {
        const key = `${pkg.type}-${pkg.name}`;
        if (pkg.custom) {
            return `${key}-custom`;
        }
        return key;
    }

    /**
     * Generates a package manifest URL with a "bazaar" query parameter.
     *
     * @param {string} manifestUrl - The original manifest URL.
     * @param {boolean} [useBazaar=true] - Flag to enable or disable the "bazaar" parameter.
     * @returns {string} - The modified manifest URL with the "bazaar" query parameter.
     */
    static getPackageManifestUrl(manifestUrl, useBazaar = true) {
        try {
            const customManifestUrl = new URL(manifestUrl);
            customManifestUrl.searchParams.set("bazaar", useBazaar ? "enabled" : "disabled");
            return customManifestUrl.toString();
        } catch {
            return manifestUrl;
        }
    }
}
